import type { ReferenceGraphemeMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { GraphemeSvgGlyphPlayer } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.svg";
import {
  Surface,
  SurfaceContent,
  SurfaceHeader,
  SurfaceTitle,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { useMemo } from "react";
import { useLearnCardSurface } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { formatStrokeCount } from "#format-stroke-count";

/**
 * A grapheme's stroke order as a surface: the stroke count in the header, and one numbered step
 * per stroke (sharing one timeline, with a Replay button) as the body.
 */
export function MorphemeInfoPanelCaligraphyStrokesSurface({
  className,
  graphemeCard,
}: {
  className?: string;
  graphemeCard: ReferenceGraphemeMorphemeSlice;
}) {
  const { isActive } = useLearnCardSurface();
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const graphemeGlyph = getGraphemeGlyph(graphemeCard.grapheme);
  // `null` (or empty) when the grapheme has no stroke data: the player renders the font glyph and
  // no count is shown, rather than "0 strokes".
  const { graphemeStrokes } = graphemeCard;
  const strokeCount = graphemeStrokes === null ? 0 : arrayLength(graphemeStrokes);
  const svgStrokeList = useMemo(
    () =>
      graphemeStrokes === null
        ? null
        : graphemeStrokes.map(({ median, path }) => ({ median, path })),
    [graphemeStrokes],
  );

  return (
    <Surface className={className} testID="caligraphy-strokes-surface">
      <SurfaceHeader className="flex-row items-center justify-between gap-2 py-2">
        <SurfaceTitle className="pt-0">Strokes</SurfaceTitle>
        {strokeCount > 0 && (
          <Text className="text-xs" colorVariant="muted">
            {formatStrokeCount(strokeCount)}
          </Text>
        )}
      </SurfaceHeader>
      <SurfaceContent className="items-center p-3">
        <GraphemeSvgGlyphPlayer
          autoPlay={isActive}
          glyph={graphemeGlyph}
          showStrokeSteps
          size={220}
          strokeList={svgStrokeList}
        />
      </SurfaceContent>
    </Surface>
  );
}
